// src/pages/WelcomePage.js
import React from 'react';
import { Link } from 'react-router-dom';
import './WelcomePage.css';
import { trackEvent } from '../analytics';
import imgDashboard from '../assets/welcome/dashboard.svg';
import imgFlow from '../assets/welcome/flow.svg';
import imgTeam from '../assets/welcome/team.svg';
import imgFeatures from '../assets/welcome/features.svg';
import imgTestimonial from '../assets/welcome/testimonial.svg';
import imgVideo from '../assets/welcome/video.svg';
import imgOffer from '../assets/welcome/offer.svg';
import imgHeroTeam from '../assets/welcome/hero-team.svg';

function KPICard({ value, label }) {
  return (
    <div className="kpi-card">
      <div className="kpi-value">{value}</div>
      <div className="kpi-label">{label}</div>
    </div>
  );
}

function WelcomePage() {
  return (
    <div className="welcome">
      <header className="welcome-hero">
        <div className="welcome-hero-inner">
          <div className="welcome-hero-copy">
            <div className="welcome-eyebrow">Agentless • Azure‑Native • 24/7</div>
            <h1>Finally HIPAA Compliance That Runs Itself.</h1>
            <p className="welcome-subtitle">The agentless, Azure-native solution that keeps you audit-ready 24/7.</p>
            <div className="welcome-cta-row">
              <a href="/.auth/login/aad" className="button" onClick={() => trackEvent('cta_start_setup_welcome_hero')}>Start Free Setup</a>
              <Link to="/pricing" className="button button-outline" onClick={() => trackEvent('cta_see_pricing_welcome_hero')}>See Plans & Pricing</Link>
            </div>
            <p className="welcome-quip">If you've ever been blindsided by an auditor, lost a day to spreadsheets, or spent nights triple-checking HIPAA controls, this is for you.</p>
          </div>
        </div>
      </header>

      <main className="welcome-content">
        <section className="welcome-section">
          <h2>The Problem: Compliance Is Eating Your Time</h2>
          <p>You didn't become an IT or security lead to manage checklists. You came to build, secure, and innovate.</p>
          <ul className="bullet-list">
            <li>Endless spreadsheets</li>
            <li>Manual review cycles</li>
            <li>Unclear audit trails</li>
            <li>And the sinking feeling that something might be out of compliance</li>
          </ul>
          <p>You're spending so much time proving you're compliant, there's no time left to stay compliant. Most mid-sized companies dread HIPAA audits because they rely on reactive compliance, not continuous monitoring.</p>
        </section>

        <section className="welcome-section">
          <h2>The Solution: Compliance Guardian</h2>
          <p className="section-sub">Continuous HIPAA Compliance for Microsoft Cloud — No Enterprise Contracts. No Consultants.</p>
          <p>Compliance Guardian connects directly to your Azure and Microsoft 365 environments and automates everything:</p>
          <ul className="bullet-list">
            <li>Continuous checks</li>
            <li>Real-time alerts</li>
            <li>Auditor-ready evidence</li>
          </ul>
          <p>You don't install anything. You don't configure agents. You just connect your tenant and relax.</p>
          <img className="welcome-img" src={imgDashboard} alt="Dashboard showing compliance scores improving in real time" />
        </section>

        <section className="welcome-section">
          <h2>How It Works (and Why It's Brilliantly Simple)</h2>
          <div className="steps-grid">
            <div className="step">
              <div className="step-num">1</div>
              <h4>Connect Your Tenant</h4>
              <p>Grant read-only access in 2 minutes — no risky permissions, no setup headaches.</p>
            </div>
            <div className="step">
              <div className="step-num">2</div>
              <h4>Activate Continuous Monitoring</h4>
              <p>We analyze your Azure and M365 configurations for HIPAA controls — 24/7.</p>
            </div>
            <div className="step">
              <div className="step-num">3</div>
              <h4>Stay Audit-Ready</h4>
              <p>Download PDF reports that auditors actually love — with timestamps, evidence, and remediation steps.</p>
            </div>
          </div>
          <img className="welcome-img" src={imgFlow} alt="Simple 3-step flow diagram" />
        </section>

        <section className="welcome-section">
          <h2>Why Mid-Sized Teams Choose Compliance Guardian</h2>
          <p>Because they don't have a dedicated compliance department, time for manual audits, or patience for enterprise sales calls — and they don't need them.</p>
          <img className="welcome-img" src={imgTeam} alt="Team reviewing compliance dashboard together" />
        </section>

        <section className="welcome-section">
          <h2>Key Features That Do the Heavy Lifting</h2>
          <div className="feature-grid">
            <div className="feature"><h4>Azure & M365 native</h4><p>Seamlessly connects with your existing environment.</p></div>
            <div className="feature"><h4>Real-time detection</h4><p>Instantly flags risky behavior and configuration drift.</p></div>
            <div className="feature"><h4>Auditor-ready reports</h4><p>Generate professional PDFs with one click.</p></div>
            <div className="feature"><h4>Zero manual spreadsheets</h4><p>Replace guesswork with automation.</p></div>
            <div className="feature"><h4>Least-privilege access</h4><p>Secure by default — no intrusive permissions.</p></div>
            <div className="feature"><h4>Full traceability</h4><p>Every alert shows who, what, when, and how it happened.</p></div>
          </div>
          <img className="welcome-img" src={imgFeatures} alt="Feature highlight grid" />
        </section>

        <section className="welcome-section testimonial">
          <blockquote className="quote">“We cut audit prep from weeks to hours. Our small security team now focuses on prevention instead of paperwork.”</blockquote>
          <div className="testimonial-meta">— Alex Rivera, Head of Security, Meridian Health</div>
          <div className="kpi-row">
            <KPICard value="90%" label="faster audits" />
            <KPICard value="24/7" label="continuous checks" />
            <KPICard value="0" label="agents required" />
          </div>
          <img className="welcome-img" src={imgTestimonial} alt="Quote testimonial card and customer logo" />
        </section>

        <section className="welcome-section">
          <h2>Why It Works So Well</h2>
          <p>Because we've removed the friction that kills compliance efforts:</p>
          <ul className="bullet-list">
            <li>No agents</li>
            <li>No contracts</li>
            <li>No manual data pulls</li>
          </ul>
          <p>Every change, every event, every violation is captured automatically. You'll always know your compliance status before anyone else does.</p>
        </section>

        <section className="welcome-section">
          <h2>Why It's Perfect for You</h2>
          <ul className="bullet-list">
            <li>Cloud engineers tired of ad-hoc audits</li>
            <li>IT managers juggling security and compliance</li>
            <li>DevOps leads who want proof of compliance without extra tools</li>
          </ul>
          <img className="welcome-img" src={imgVideo} alt="Video placeholder with play button" />
        </section>

        <section className="welcome-section pricing-callout">
          <h2>Simple, Transparent Pricing</h2>
          <p>No enterprise pricing games. No calls with account executives. No multi-year commitments.</p>
          <p>One affordable monthly plan with everything included.</p>
          <div className="welcome-cta-row">
            <Link to="/pricing" className="button button-outline" onClick={() => trackEvent('cta_see_pricing_welcome_pricingcallout')}>See Plans & Pricing</Link>
          </div>
        </section>

        <section className="welcome-section cta-band">
          <h2>Ready to Stay HIPAA-Compliant — Automatically?</h2>
          <p>No spreadsheets. No agent installs. No scrambling before audits. Just peace of mind, 24/7.</p>
          <img className="welcome-img" src={imgHeroTeam} alt="Team celebrating improvements in compliance dashboard results" />
          <div className="welcome-cta-row">
            <a href="/.auth/login/aad" className="button" onClick={() => trackEvent('cta_start_setup_welcome_midband')}>Start Free Setup</a>
            <Link to="/pricing" className="button button-outline" onClick={() => trackEvent('cta_see_pricing_welcome_midband')}>See Plans & Pricing</Link>
          </div>
        </section>

        <section className="welcome-section offer">
          <h2>Bonus Offer</h2>
          <p>Sign up this week and get your first month of continuous monitoring free — no strings attached.</p>
          <img className="welcome-img" src={imgOffer} alt="Offer badge for free first month" />
        </section>

        <section className="welcome-section trust">
          <h2>Built for Trust</h2>
          <ul className="pill-list">
            <li>Azure-native</li>
            <li>HIPAA-aligned</li>
            <li>Read-only permissions</li>
            <li>End-to-end transparency</li>
          </ul>
          <p>You're always in control — we just handle the heavy lifting.</p>
        </section>

        <section className="welcome-section final-cta">
          <h2>Be Audit-Ready — All the Time</h2>
          <p>Stop guessing. Start automating.</p>
          <div className="welcome-cta-row">
            <a href="/.auth/login/aad" className="button" onClick={() => trackEvent('cta_start_setup_welcome_final')}>Start Free Setup Now</a>
            <Link to="/pricing" className="button button-outline" onClick={() => trackEvent('cta_see_pricing_welcome_final')}>See Plans & Pricing</Link>
          </div>
        </section>
      </main>
      <div style={{ padding: '2rem', textAlign: 'center', backgroundColor: '#f8f9fa' }}>
        <a href="https://nxgntools.com/tools/compliance-guardian"
          target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', width: 'auto' }}>
          <img src="https://nxgntools.com/api/embed/compliance-guardian?type=FEATURED_ON&hideUpvotes=true"
            alt="Compliance Guardian on NextGen Tools" style={{ height: '48px', width: 'auto' }} />
        </a>
      </div>
    </div>
  );
}

export default WelcomePage;

